/**
 * EntriesPage Component
 *
 * Main entries view page showing all logged entries for a selected date.
 * Allows editing and deleting entries with appropriate role-based access.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiUrl } from '../lib/api';
import { getLocalDateString, addDaysToDate } from '../lib/dateUtils';
import { DatePicker } from '../components/ui/DatePicker';
import { EntryList, TypedEntry } from '../components/entries/EntryList';
import { EditEntryModal } from '../components/entries/EditEntryModal';
import { DeleteConfirmDialog } from '../components/entries/DeleteConfirmDialog';
import {
	AnyEntry,
	EntryType,
	WeightEntry,
	MealEntry,
	ExerciseEntry,
	HealthMetricEntry,
	MedicalVisit,
	Prescription,
	ActivityEntry,
	getEntrySummary,
} from '../components/entries/EntryCard';


/**
 * Get API endpoint for entry type
 */
function getApiEndpoint(type: EntryType): string {
	switch (type) {
		case 'weight':
			return apiUrl('/api/weight');
		case 'meal':
			return apiUrl('/api/meals');
		case 'exercise':
			return apiUrl('/api/exercise');
		case 'health':
			return apiUrl('/api/health-metrics');
		case 'visit':
			return apiUrl('/api/medical/visits');
		case 'prescription':
			return apiUrl('/api/medical/prescriptions');
		case 'activity':
			return apiUrl('/api/activities');
	}
}

export function EntriesPage() {
	const { user } = useAuth();
	const [searchParams] = useSearchParams();
	const initialDate = searchParams.get('date') || getLocalDateString();
	const [selectedDate, setSelectedDate] = useState(initialDate);
	const [entries, setEntries] = useState<TypedEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Edit state
	const [editingEntry, setEditingEntry] = useState<{
		entry: AnyEntry;
		type: EntryType;
	} | null>(null);

	// Delete state
	const [deletingEntry, setDeletingEntry] = useState<{
		entry: AnyEntry;
		type: EntryType;
	} | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Check if user can modify entries (editor, admin, superadmin)
	const canModify = user?.role === 'editor' || user?.role === 'admin' || user?.role === 'superadmin';

	/**
	 * Fetch all entries for the selected date
	 */
	const fetchEntries = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			// Single API call for all entry types
			const res = await fetch(apiUrl(`/api/entries?date=${selectedDate}`), {
				credentials: 'include',
			});

			if (!res.ok) {
				throw new Error('Failed to fetch entries');
			}

			const data = await res.json();

			// Combine all entries with their types
			const allEntries: TypedEntry[] = [];

			// Add weight entries
			if (data.weight) {
				data.weight.forEach((entry: WeightEntry) => {
					allEntries.push({ entry, type: 'weight' });
				});
			}

			// Add meal entries
			if (data.meals) {
				data.meals.forEach((entry: MealEntry) => {
					allEntries.push({ entry, type: 'meal' });
				});
			}

			// Add exercise entries
			if (data.exercise) {
				data.exercise.forEach((entry: ExerciseEntry) => {
					allEntries.push({ entry, type: 'exercise' });
				});
			}

			// Add health entries
			if (data.health) {
				data.health.forEach((entry: HealthMetricEntry) => {
					allEntries.push({ entry, type: 'health' });
				});
			}

			// Add visit entries
			if (data.visits) {
				data.visits.forEach((visit: MedicalVisit) => {
					allEntries.push({ entry: visit, type: 'visit' });
				});
			}

			// Add prescription entries
			if (data.prescriptions) {
				data.prescriptions.forEach((prescription: Prescription) => {
					allEntries.push({ entry: prescription, type: 'prescription' });
				});
			}

			// Add activity entries
			if (data.activities) {
				data.activities.forEach((activity: ActivityEntry) => {
					allEntries.push({ entry: activity, type: 'activity' });
				});
			}

			setEntries(allEntries);
		} catch (err) {
			console.error('Failed to fetch entries:', err);
			setError('Gagal memuat data. Silakan coba lagi.');
		} finally {
			setLoading(false);
		}
	}, [selectedDate]);

	// Fetch entries when date changes
	useEffect(() => {
		fetchEntries();
	}, [fetchEntries]);

	/**
	 * Handle edit button click
	 */
	const handleEdit = (entry: AnyEntry, type: EntryType) => {
		setEditingEntry({ entry, type });
	};

	/**
	 * Handle delete button click
	 */
	const handleDelete = (entry: AnyEntry, type: EntryType) => {
		setDeletingEntry({ entry, type });
	};

	/**
	 * Handle edit save
	 */
	const handleEditSave = () => {
		setEditingEntry(null);
		fetchEntries();
	};

	/**
	 * Handle delete confirm
	 */
	const handleDeleteConfirm = async () => {
		if (!deletingEntry) return;

		setIsDeleting(true);

		try {
			const endpoint = getApiEndpoint(deletingEntry.type);
			const res = await fetch(`${endpoint}/${deletingEntry.entry.id}?confirm=true`, {
				method: 'DELETE',
				credentials: 'include',
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menghapus data');
			}

			setDeletingEntry(null);
			fetchEntries();
		} catch (err) {
			console.error('Failed to delete entry:', err);
			// Error is handled by keeping the dialog open
		} finally {
			setIsDeleting(false);
		}
	};

	/**
	 * Navigate to previous day
	 */
	const goToPreviousDay = () => {
		setSelectedDate(addDaysToDate(selectedDate, -1));
	};

	/**
	 * Navigate to next day (but not beyond today)
	 */
	const goToNextDay = () => {
		const today = getLocalDateString();
		const nextDay = addDaysToDate(selectedDate, 1);
		if (nextDay <= today) {
			setSelectedDate(nextDay);
		}
	};

	/**
	 * Navigate to today
	 */
	const goToToday = () => {
		setSelectedDate(getLocalDateString());
	};

	const isToday = selectedDate === getLocalDateString();
	const canGoNext = !isToday;

	return (
		<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 sm:pb-6">
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Catatan Harian</h1>
				<p className="text-gray-600 mt-1">
					Lihat dan kelola catatan kesehatan harian
				</p>
			</div>

			{/* Date Selection */}
			<div className="bg-white shadow rounded-lg p-4 mb-4">
				<div className="flex items-center gap-4">
					{/* Previous Day Button */}
					<button
						onClick={goToPreviousDay}
						className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
						aria-label="Hari sebelumnya"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-5 w-5"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
								clipRule="evenodd"
							/>
						</svg>
					</button>

					{/* Date Picker */}
					<div className="flex-1">
						<DatePicker
							value={selectedDate}
							onChange={setSelectedDate}
							label=""
							id="entries-date"
						/>
					</div>

					{/* Next Day Button */}
					<button
						onClick={goToNextDay}
						disabled={!canGoNext}
						className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
						aria-label="Hari berikutnya"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-5 w-5"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
								clipRule="evenodd"
							/>
						</svg>
					</button>

					{/* Today Button */}
					{!isToday && (
						<button
							onClick={goToToday}
							className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
						>
							Hari Ini
						</button>
					)}
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
					{error}
					<button
						onClick={fetchEntries}
						className="ml-2 text-red-800 underline hover:no-underline"
					>
						Coba lagi
					</button>
				</div>
			)}

			{/* Entry List */}
			<div className="bg-white shadow rounded-lg p-4 sm:p-6">
				<EntryList
					entries={entries}
					date={selectedDate}
					onEdit={handleEdit}
					onDelete={handleDelete}
					canModify={canModify}
					loading={loading}
				/>
			</div>

			{/* Add Entry Link - Floating on mobile, inline on desktop */}
			{canModify && (
				<>
					{/* Desktop version - inline */}
					<div className="hidden sm:block mt-6 text-center">
						<Link
							to="/log"
							className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
									clipRule="evenodd"
								/>
							</svg>
							Tambah Catatan
						</Link>
					</div>
					{/* Mobile version - fixed floating bar at bottom */}
					<div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-20">
						<Link
							to="/log"
							className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium min-h-12"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
									clipRule="evenodd"
								/>
							</svg>
							Tambah Catatan
						</Link>
					</div>
				</>
			)}

			{/* Edit Modal */}
			<EditEntryModal
				entry={editingEntry?.entry || null}
				entryType={editingEntry?.type || null}
				isOpen={editingEntry !== null}
				onClose={() => setEditingEntry(null)}
				onSave={handleEditSave}
			/>

			{/* Delete Confirm Dialog */}
			<DeleteConfirmDialog
				isOpen={deletingEntry !== null}
				onClose={() => setDeletingEntry(null)}
				onConfirm={handleDeleteConfirm}
				entryDescription={
					deletingEntry
						? getEntrySummary(deletingEntry.entry, deletingEntry.type)
						: ''
				}
				loading={isDeleting}
			/>
		</div>
	);
}
